import { SupportedChainId } from "@/config/constants/chains";
import {
	getV1FactoryAddress,
	getV2FactoryAddress,
} from "@/utils/addressHelpers";
import { ChainId } from "@vapordex/sdk";
import {
	MEME_FEES_DISTRIBUTION_URL,
	STRATOSPHERE_POINTS_PONDER_URL,
	TOKEN_FACTORY_SUBGRAPH_URLS,
	V2_SUBGRAPH_URLS,
	VAPORDEX_V1_SUBGRAPH,
} from "config/constants/endpoints";
import { GraphQLClient, gql } from "graphql-request";
import useActiveWagmi from "hooks/useActiveWagmi";
import useSWR from "swr";

export const memeIndexerClient = new GraphQLClient(
	MEME_FEES_DISTRIBUTION_URL[SupportedChainId.CURTIS],
);
export const v2PoolsClients = Object.fromEntries(
	Object.entries(V2_SUBGRAPH_URLS).map(([chainId, url]) => [
		Number(chainId) as SupportedChainId,
		new GraphQLClient(url),
	]),
) as Record<SupportedChainId, GraphQLClient>;

export const v1PoolsClients = Object.fromEntries(
	Object.entries(VAPORDEX_V1_SUBGRAPH).map(([chainId, url]) => [
		Number(chainId),
		new GraphQLClient(url),
	]),
);

export const stratospherePointsClients = {
	[ChainId.AVALANCHE]: new GraphQLClient(
		STRATOSPHERE_POINTS_PONDER_URL[ChainId.AVALANCHE],
	),
	[ChainId.AVALANCHE_TESTNET]: new GraphQLClient(
		STRATOSPHERE_POINTS_PONDER_URL[ChainId.AVALANCHE_TESTNET],
	),
};

export const tokenFactoryClients = {
	[ChainId.AVALANCHE]: new GraphQLClient(
		TOKEN_FACTORY_SUBGRAPH_URLS[ChainId.AVALANCHE],
	),
	[ChainId.AVALANCHE_TESTNET]: new GraphQLClient(
		TOKEN_FACTORY_SUBGRAPH_URLS[ChainId.AVALANCHE_TESTNET],
	),
} as const;

export const memeFeesClients = Object.fromEntries(
	Object.entries(MEME_FEES_DISTRIBUTION_URL || {}).map(([chainId, url]) => [
		Number(chainId),
		new GraphQLClient(url),
	]),
);

export const getV1PoolsSubgraphQuery = async (chainId: string) => {
	const parsedChainId = Number(chainId);
	const client = v1PoolsClients[parsedChainId];
	if (!client) return [];

	return await client
		.request(
			gql`
        {
          pairs(first: 1000) {
            id
            token0 {
              id
              symbol
              name
              decimals
            }
            token1 {
              id
              symbol
              name
              decimals
            }
            reserve0
            reserve1
            reserveUSD
            volumeUSD
            pairDayData(first: 7, orderDirection: desc, orderBy: date) {
              dailyVolumeUSD
            }
          }
        }
      `,
		)
		.then((res: any) =>
			res?.pairs?.map((pair) => ({
				...pair,
				cumulativeVolumeUSD: pair?.volumeUSD,
				dailySnapshots: pair?.pairDayData,
				inputTokenBalances: [pair?.reserve0, pair?.reserve1],
				inputTokens: [pair?.token0, pair?.token1],
				totalValueLockedUSD: pair?.reserveUSD,
			})),
		)
		.catch((error) => {
			console.error(
				`Error querying v1 subgraph (chainId: ${chainId}): ${error}`,
			);
			return [];
		});
};

export const getV2PoolsSubgraphQuery = async (key: string) => {
	const [chainId, isV2Enabled] = key.split("-");

	if (isV2Enabled === "0") return [];

	const parsedChainId = Number(chainId);
	const client = v2PoolsClients[parsedChainId];

	if (!client) return [];

	const v2Query = gql`
	  query v2PoolsData {
		pools(first: 1000) {
		  id
		  totalValueLockedUSD
		  totalValueLockedToken0
		  totalValueLockedToken1
		  feeTier
		  volumeUSD
		  token0Price
		  token1Price
		  token0 {
			derivedAVAX
			name
			symbol
			id
			decimals
		  }
		  token1 {
			derivedAVAX
			name
			symbol
			id
			decimals
		  }
		  poolWeekData {
			volumeUSD
		  }
		  poolDayData(first: 1, orderBy: date, orderDirection: desc) {
			volumeUSD
		  }
		  collectedFeesUSD
		}
	  }
	`;
	return await client
		.request(v2Query)
		.then((res) => res?.pools)
		.catch((error) => {
			console.error(
				`Error in querying v2 subgraph (chainId: ${chainId}): ${error}`,
			);
			return [];
		});
};

interface TokenBurnResponse {
	token?: {
		totalBurned: string;
	};
}

export const getTokenBurnData = async (
	tokenAddress: string,
	chainId: string,
) => {
	try {
		const parsedChainId = Number(chainId);
		const client = memeFeesClients[parsedChainId];

		if (!client || !tokenAddress) return "0";

		const query = gql`
		query getTokenBurnData($tokenAddress: String!) {
		  token(id: $tokenAddress) {
			totalBurned
		  }
		}
	  `;

		const result = await client.request<TokenBurnResponse>(query, {
			tokenAddress: tokenAddress.toLowerCase(),
		});

		if (result?.token?.totalBurned) {
			const burnedTokensInWei = BigInt(result.token.totalBurned);
			const burnedTokens = Number(burnedTokensInWei) / 10 ** 18;
			return burnedTokens.toString();
		}

		return "0";
	} catch (error) {
		console.error(
			`Error fetching token burn data for ${tokenAddress}: ${error}`,
		);
		return "0";
	}
};

export const getTotalValueLocked = async (chainId: string) => {
	try {
		const parsedChainId = Number(chainId);
		const client1 = v1PoolsClients[parsedChainId];
		const client2 = v2PoolsClients[parsedChainId];
		const v1FactoryAddress = getV1FactoryAddress(parsedChainId)?.toLowerCase();
		const v2FactoryAddress = getV2FactoryAddress(parsedChainId)?.toLowerCase();

		const v1TVL = await client1
			?.request(
				gql`
			query v1TVLData {
			  uniswapFactory(id: "${v1FactoryAddress}") {
				totalLiquidityUSD
			  }
			}
		  `,
			)
			.then((res: any) => res?.uniswapFactory?.totalLiquidityUSD || "0")
			.catch(() => "0");

		const v2TVL = await client2
			?.request(
				gql`
			query v2TVLData {
			  factory(id: "${v2FactoryAddress}") {
				totalValueLockedUSD
			  }
			}
		  `,
			)
			.then((res) => res?.factory?.totalValueLockedUSD || "0")
			.catch(() => "0");

		return Number.parseFloat(v1TVL) + Number.parseFloat(v2TVL);
	} catch {
		return 0;
	}
};

export const useTotalValueLocked = () => {
	const { chainId } = useActiveWagmi();
	const { data } = useSWR(
		chainId ? `tvl-${chainId}` : null,
		() => getTotalValueLocked(chainId.toString()),
		{
			refreshInterval: 30000,
			dedupingInterval: 5000,
			fallbackData: 0,
		},
	);

	return data;
};
